import Button from "components/form/Button";
import { ExpiredLink } from "icons/action/ExpiredLink";
import { FilledInfoIcon } from "icons/payments/FilledInfoIcon";
import OutlineInfoIcon from "icons/payments/OutlineInfoIcon";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "store/hooks";
import { addNotification, useUserData } from "store/slices/authSlice";
import { sendEmailRenew } from "store/slices/paymentsSlice";
import { decode, encode } from "utils/HelperFunctions";
import { useTestAttrsFactory } from "utils/testid";

const PaymentLinkExpired = ({
  setIsLinkSent,
  className,
  setIsLinkSentSuccess,
  setIsLinkSentFailedReason,
}: any) => {
  const { id: actionId } = useParams();
  const clusterId = decode(actionId || "")?.split("_")[1];
  let cluster_id = encode(`${clusterId}`, "ci_");

  const userData = useUserData();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const testAttrs = useTestAttrsFactory();
  const sendRenewEmail = async () => {
    setIsLoading(true);
    let slug = decode(actionId || "")?.split("_");
    try {
      if (!slug?.[0] || !clusterId) {
        console.warn("sendRenewEmail skipped: missing required values", {
          payments_id: slug?.[0],
          cluster_id: clusterId,
        });
        return;
      }

      let payload = {
        payments_id: slug[0],
        cluster_id: clusterId,
      };
      let res: any = await dispatch(sendEmailRenew(payload)).unwrap();
      if (res.status === 200 || res.status === 201) {
        const { data, status } = res?.body;
        setIsLinkSent(true);
        if (status === true) {
          setIsLinkSentSuccess(true);
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_link",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "requested",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: data?.amount,
              payment_link_id: slug[0],
            })
          );
        } else {
          setIsLinkSentFailedReason(
            (typeof data === "object" && data !== null ? data.msg : data) ||
              "Payment Link Renew Request Failed"
          );
          setIsLinkSentSuccess(false);
        }
      }
    } catch (error) {
      console.log("sendRenewEmail error =====", error);
    } finally {
      setIsLoading(false);
    }
  };
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  return (
    <div
      className={`flex items-center justify-center h-full flex-col text-center px-5 lg:pb-0 pb-10 max-w-[630px] ${className}`}
      {...testAttrs("expired", "Payment link expired")}
    >
      <ExpiredLink aria-hidden="true" />
      <div className="lg:text-2xl text-xl mt-10">Payment link expired</div>
      <div className="text-sm lg:text-base mt-4 text-txtParagraph">
        Oops! It looks like the payment link you're trying to access has
        expired.
      </div>
      <div className="text-sm lg:text-base mt-4 text-txtParagraph">
        No problem! Simply click the button below to request a new payment link.
        We'll review your request and send a fresh link to your email as soon as
        possible.
      </div>

      <div className="text-[13px] inline-flex gap-[5px] mt-10 text-txtParagraph justify-center text-center px-14">
        <span className="w-[14px] h-[14px] mt-[2px]">
          <OutlineInfoIcon aria-hidden="true" />
        </span>
        <span className="w-full max-w-[380px]">
          Please note, the issuance of a new link is subject to review.
        </span>
      </div>

      <div className="mt-10">
        <Button
          text="Request new link"
          className={`w-fit !px-4 !py-[10px]`}
          {...testAttrs("request-new-link", "Request new payment link")}
          onClick={() => sendRenewEmail()}
          isLoading={isLoading}
        />
      </div>
      <div
        className="mt-6  lg:text-base text-sm cursor-pointer text-blue hover:!text-btnPrimaryHover"
        role="button"
        tabIndex={0}
        {...testAttrs("back-to-dashboard", "Back to dashboard")}
        onClick={() => {
          navigate(`/${cluster_id}/dashboard`);
        }}
      >
        Back to dashboard
      </div>
    </div>
  );
};

export default PaymentLinkExpired;
